import React from "react";
import { Button } from "./Button";
import { useEditableForm } from "@/stores/useEditableFormStore";
import { cn } from "@/lib/utils/cn";

export interface FormActionsProps {
  onCancel?: () => void;
  isPending?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  showCancel?: boolean;
  align?: "left" | "center" | "right";
  formId?: string;
  className?: string;
}

export function FormActions({
  onCancel,
  isPending = false,
  submitLabel = "Save Changes",
  cancelLabel = "Cancel",
  showCancel = true,
  align = "right",
  formId,
  className = "",
}: FormActionsProps) {
  const {
    forms,
    isEditing: globalIsEditing,
    editableMode: globalEditableMode,
    cancelEditing,
  } = useEditableForm();

  const isEditing = formId
    ? forms[formId]?.isEditing ?? false
    : globalIsEditing;

  const editableMode = formId
    ? forms[formId]?.editableMode ?? false
    : globalEditableMode;

  const alignmentClass = {
    left: "justify-start",
    center: "justify-center",
    right: "justify-end",
  }[align];

  const handleCancel = () => {
    if (editableMode) {
      cancelEditing(formId);
    }
    onCancel?.();
  };

  const shouldShowCancel = showCancel && (editableMode || onCancel);

  // Only show buttons when in edit mode
  if (!isEditing) {
    return null;
  }

  return (
    <div className={cn("flex items-center gap-3", alignmentClass, className)}>
      {shouldShowCancel && (
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={isPending}
        >
          {cancelLabel}
        </Button>
      )}

      <Button type="submit" variant="primary" disabled={isPending}>
        {isPending ? (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Saving...
          </div>
        ) : (
          submitLabel
        )}
      </Button>
    </div>
  );
}
